import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import Button, { TYPES } from 'components/Button';
import Paragraph from 'components/Paragraph';
import Radio from 'components/Radio';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import { INVENTORY_LEVELS, INVENTORY_LABELS } from './constants';

const StoreInfo = ({
  sortedStores,
  storeList,
  defaultSortedList,
  handleSetPreferredStore,
  preferredStore,
  storeSelected,
  storeSaved,
}) => {
  const [selectedStoreHandle, setSelectedStoreHandle] = useState(preferredStore?.handle);
  let sortedStoresList;
  if (sortedStores && sortedStores.length > 0) {
    sortedStoresList = sortedStores;
  } else {
    sortedStoresList = defaultSortedList;
  }

  return (
    <div className="StoreInfo">
      <form
        className="StoreInfo__form"
        onSubmit={event => {
          event.preventDefault();
          handleSetPreferredStore(storeList[selectedStoreHandle]);
        }}
      >
        <div className="StoreInfo__details">
          {sortedStoresList.map(store => (
            <div
              className={classnames('StoreInfo__details-container', {
                'StoreInfo__details--no-inventory':
                  storeList[store.handle]?.inventoryLevel === INVENTORY_LEVELS.NO_INVENTORY,
                'StoreInfo__details--low-inventory':
                  storeList[store.handle]?.inventoryLevel === INVENTORY_LEVELS.LOW_INVENTORY,
                'StoreInfo__details--high-inventory':
                  storeList[store.handle]?.inventoryLevel === INVENTORY_LEVELS.HIGH_INVENTORY,
              })}
              key={`store-info-${store.handle}`}
            >
              <Radio
                className="StoreInfo__option"
                name="Store Selector"
                isChecked={selectedStoreHandle === store.handle}
                value={store.handle}
                ariaLabel={I18n.localize('pdp.storeInventory.modal.savePreferredStore')}
                onChange={() => {
                  setSelectedStoreHandle(store.handle);
                  storeSelected(store.handle);
                }}
              >
                <Paragraph className="StoreInfo__option__store-inventory">
                  {INVENTORY_LABELS[storeList[store.handle]?.inventoryLevel]}
                </Paragraph>
                <Paragraph className="StoreInfo__option__store-name">
                  {storeList[store.handle]?.storeName}
                </Paragraph>
                <Paragraph>{storeList[store.handle]?.streetAddress}</Paragraph>
                <Paragraph>{storeList[store.handle]?.phoneNumber}</Paragraph>
                {store.distanceBetweenUs && (
                  <Paragraph>{Math.round(store.distanceBetweenUs)} miles away</Paragraph>
                )}
              </Radio>
            </div>
          ))}
        </div>

        <div className="StayInTouchEmailSignUpForm__button-cell">
          <Button
            isSubmit
            isFullWidth
            isDisabled={!selectedStoreHandle}
            type={TYPES.DEFAULT}
            id="StayInTouchEmailSignUpForm__submit-button"
            onClick={() => storeSaved(selectedStoreHandle)}
          >
            {I18n.localize('pdp.storeInventory.modal.savePreferredStore')}
          </Button>
        </div>
      </form>

      <style jsx>
        {`
          .StoreInfo {
            position: relative;
            display: flex;
            background-color: ${Theme.get('colors-white')};
            overflow: hidden;
          }

          .StoreInfo__form {
            width: 100%;
            display: flex;
            flex-direction: column;
          }

          .StoreInfo__details-container {
            display: flex;
            margin-bottom: ${Theme.get('spacing') * 3}px;
            // 1px to handle active button state
            margin-right: 1px;
          }
        `}
      </style>
      <style jsx global>
        {`
          .StoreInfo__option.Radio {
            flex-direction: row-reverse;
            flex-grow: 1;
            justify-content: space-between;
          }

          .StoreInfo__option .Radio__label {
            margin-left: 0;
          }

          .StoreInfo__option .Paragraph {
            margin-bottom: ${Theme.get('spacing') / 2}px;
          }

          .Paragraph.StoreInfo__option__store-name {
            font-weight: 700;
          }

          .Paragraph.StoreInfo__option__store-inventory {
            font-weight: 700;
          }

          .StoreInfo__details--no-inventory .Paragraph.StoreInfo__option__store-inventory {
            color: ${Theme.get('colors-discount-grey')};
          }

          .StoreInfo__details--low-inventory .Paragraph.StoreInfo__option__store-inventory {
            color: ${Theme.get('colors-discount-yellow')};
          }

          .StoreInfo__details--high-inventory .Paragraph.StoreInfo__option__store-inventory {
            color: ${Theme.get('colors-success')};
          }

          .StoreInfo__details {
            overflow: auto;
            scroll-behavior: smooth;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
          }

          /* Hide scrollbar for Chrome, Safari and Opera */
          .StoreInfo__details::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </div>
  );
};

StoreInfo.propTypes = {
  handleSetPreferredStore: PropTypes.func.isRequired,
  sortedStores: PropTypes.arrayOf(
    PropTypes.shape({
      handle: PropTypes.string.isRequired,
    })
  ),
  defaultSortedList: PropTypes.arrayOf(
    PropTypes.shape({
      handle: PropTypes.string.isRequired,
    })
  ),
  storeList: PropTypes.shape({
    handle: PropTypes.string,
    streetAddress: PropTypes.string,
    storeName: PropTypes.string,
    phoneNumber: PropTypes.string,
    inventoryLevel: PropTypes.string,
  }),
  preferredStore: PropTypes.shape({
    handle: PropTypes.string,
  }),
  storeSelected: PropTypes.func.isRequired,
  storeSaved: PropTypes.func.isRequired,
};

StoreInfo.defaultProps = {
  preferredStore: {
    handle: null,
  },
};

export default StoreInfo;
